






































































































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { get as _get } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { isUserAllowed } from '@/utils/rbac.util';
import { PermissionsMatrixActionsEnum } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import FHIRConfirmModal from './FHIRConfirmModal.vue';
import {
  FHIRDefaultParameter,
  FHIRDownloadSettings
} from '@/store/modules/fhir/types/fhir.types';
import {
  CreateFHIRClientDownloadPermission,
  CreateFHIRClientParameterPermission,
  FHIRClient,
  UpdateFHIRClientPayload
} from '../types/admin-fhir-access-management.types';

interface SearchParamState {
  id: number;
  displayName: string;
  key: string;
  type: string;
  isEnabled: boolean;
  defaultAccessChosen: 'all_clients' | 'mixed' | 'configured';
  clientAccessChosen: 'all_clients' | 'configured';
  maxCardinality: number | undefined;
  defaultCardinality: number | undefined;
  isCardinalityFixed: boolean;
  selectedCardinality: number;
  selectedRadioValue: number | undefined;
  listValues: Array<{
    id: number;
    optionLabel: string;
    accessChosen: 'all_clients' | 'configured';
    isSelected: boolean;
  }>;
}

interface DownloadFormat {
  formatTypeId: number;
  formatType: string;
  isEnabled: boolean;
}

function toDisplayName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

@Component({
  components: { BaseLoading }
})
export default class FHIRApiAccessTab extends Vue {
  @Prop({ required: true })
  public client!: FHIRClient;

  public form = {
    isSearchActive: false,
    isDownloadActive: false
  };

  public searchParamStates: SearchParamState[] = [];
  public downloadFormats: DownloadFormat[] = [];
  public maxDocMode: 'set-limit' | 'unlimited' = 'set-limit';
  public downloadLimit = 1;

  private savedSnapshot = '';

  @Action('fhir/getDefaultParameters')
  getDefaultParameters!: () => void;

  @Action('fhir/getDefaultDownloadPermissions')
  getDefaultDownloadPermissions!: () => void;

  @Action('fhir/updateFhirClient')
  updateFhirClient!: (args: {
    id: number;
    payload: UpdateFHIRClientPayload;
  }) => Promise<void>;

  @Action('fhir/updateClientParameters')
  updateClientParameters!: (args: {
    id: number;
    parameters: CreateFHIRClientParameterPermission[];
  }) => Promise<void>;

  @Action('fhir/updateClientDownload')
  updateClientDownload!: (args: {
    id: number;
    payload: CreateFHIRClientDownloadPermission;
  }) => Promise<void>;

  @State(({ fhir }: RootState) => fhir.defaultParameters)
  defaultParameters!: FHIRDefaultParameter[] | undefined;

  @State(({ fhir }: RootState) => fhir.downloadSettings)
  downloadSettings!: FHIRDownloadSettings | undefined;

  @State(({ fhir }: RootState) => fhir.apiState.getDefaultParameters.loading)
  isLoadingParams!: boolean;

  @State(
    ({ fhir }: RootState) => fhir.apiState.getDefaultDownloadPermissions.loading
  )
  isLoadingDownload!: boolean;

  @State(({ fhir }: RootState) => fhir.apiState.updateFhirClient)
  updateFhirClientState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.updateClientParameters)
  updateClientParametersState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.updateClientDownload)
  updateClientDownloadState!: ApiState;

  get isLoadingDefaults(): boolean {
    return (
      (this.isLoadingParams && !this.defaultParameters) ||
      (this.isLoadingDownload && !this.downloadSettings)
    );
  }

  get isSaving(): boolean {
    return (
      this.updateFhirClientState?.loading ||
      this.updateClientParametersState?.loading ||
      this.updateClientDownloadState?.loading ||
      false
    );
  }

  get canEdit(): boolean {
    return isUserAllowed(
      PermissionsMatrixActionsEnum.UPDATE,
      'fhir_access_management-update'
    );
  }

  get bothEndpointsDisabled(): boolean {
    return !this.form.isSearchActive && !this.form.isDownloadActive;
  }

  public isChecklistEmpty(param: SearchParamState): boolean {
    return (
      param.isEnabled &&
      param.type === 'checklist' &&
      !param.listValues.some((lv) => lv.isSelected)
    );
  }

  get isFormValid(): boolean {
    return (
      !this.bothEndpointsDisabled &&
      !this.searchParamStates.some((p) => this.isChecklistEmpty(p))
    );
  }

  public created() {
    if (!this.defaultParameters) this.getDefaultParameters();
    if (!this.downloadSettings) this.getDefaultDownloadPermissions();
    if (this.defaultParameters && this.downloadSettings) this.seedFromClient();
  }

  @Watch('defaultParameters')
  @Watch('downloadSettings')
  public onCatalogLoaded() {
    if (this.defaultParameters && this.downloadSettings) this.seedFromClient();
  }

  private seedFromClient() {
    this.form.isSearchActive = this.client.isSearchActive;
    this.form.isDownloadActive = this.client.isDownloadActive;

    const permByParamId = new Map(
      this.client.parameterPermission.map((p) => [p.parameterId, p])
    );

    this.searchParamStates = (this.defaultParameters ?? []).map((p) => {
      const perm = permByParamId.get(p.id);
      const hasSavedSelection = !!perm && perm.permissionListMaps.length > 0;
      const listValues = (p.listValues ?? []).map((lv) => ({
        id: lv.id,
        optionLabel: lv.optionLabel,
        accessChosen: lv.accessChosen as 'all_clients' | 'configured',
        isSelected: hasSavedSelection
          ? perm.permissionListMaps.some((m) => m.listValueId === lv.id)
          : lv.accessChosen === 'all_clients' || lv.isDefault
      }));
      const selectedListValue = listValues.find((lv) => lv.isSelected);
      const rawClientAccess = perm ? perm.accessChosen : p.accessChosen;

      return {
        id: p.id,
        displayName: toDisplayName(p.name),
        key: p.name,
        type: p.type,
        isEnabled: perm ? perm.isActive : p.accessChosen !== 'configured',
        defaultAccessChosen: p.accessChosen as
          | 'all_clients'
          | 'mixed'
          | 'configured',
        clientAccessChosen:
          rawClientAccess === 'mixed' ? 'configured' : rawClientAccess,
        maxCardinality: p.maxCardinality > 0 ? p.maxCardinality : undefined,
        defaultCardinality:
          p.defaultCardinality > 0 ? p.defaultCardinality : undefined,
        isCardinalityFixed: p.isCardinalityFixed,
        selectedCardinality: perm?.cardinality || p.defaultCardinality || 1,
        selectedRadioValue: selectedListValue?.id ?? listValues[0]?.id,
        listValues
      };
    });

    const enabledFormatIds = new Set(
      (this.client.downloadParameterPermission?.formatPermissionMaps ?? []).map(
        (m) => m.formatId
      )
    );
    this.downloadFormats = (this.downloadSettings?.formats ?? []).map((f) => ({
      formatTypeId: f.formatTypeId,
      formatType: f.formatType,
      isEnabled: enabledFormatIds.has(f.formatTypeId)
    }));
    this.maxDocMode = this.client.downloadParameterPermission?.isUnlimited
      ? 'unlimited'
      : 'set-limit';
    this.downloadLimit =
      this.client.downloadParameterPermission?.downloadLimit ?? 1;

    this.savedSnapshot = this.currentSnapshot();
  }

  private currentSnapshot(): string {
    return JSON.stringify({
      form: this.form,
      searchParamStates: this.searchParamStates,
      downloadFormats: this.downloadFormats,
      maxDocMode: this.maxDocMode,
      downloadLimit: this.downloadLimit
    });
  }

  public get isDirty(): boolean {
    return this.currentSnapshot() !== this.savedSnapshot;
  }

  public accessLabel(access: string): string {
    const map: Record<string, string> = {
      all_clients: 'ALL_CLIENTS',
      mixed: 'MIXED',
      configured: 'CONFIGURED'
    };
    return map[access] ?? access.toUpperCase();
  }

  public handleCancel() {
    if (!this.isDirty) return;
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Discard Changes?',
        modalContent:
          "Your changes haven't been saved. All edits to API access will be lost.",
        confirmBtnText: 'Discard',
        cancelBtnText: 'Continue Editing'
      },
      events: {
        confirm: () => this.seedFromClient()
      }
    });
  }

  public handleSave() {
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Save API Access Changes?',
        modalContent:
          'This will update the endpoint access, search permissions, and download permissions for this client.',
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => this.submitChanges()
      }
    });
  }

  private async submitChanges() {
    const parameters: CreateFHIRClientParameterPermission[] = this.searchParamStates.map(
      (p) => {
        const sp: CreateFHIRClientParameterPermission = {
          parameterId: p.id,
          isActive: p.isEnabled,
          cardinality: p.maxCardinality ? p.selectedCardinality : 0,
          accessChosen: p.clientAccessChosen
        };
        if (p.isEnabled) {
          if (p.type === 'radioList' && p.selectedRadioValue !== undefined) {
            sp.listValueIds = [p.selectedRadioValue];
          }
          if (p.type === 'checklist') {
            sp.listValueIds = p.listValues
              .filter((lv) => lv.isSelected)
              .map((lv) => lv.id);
          }
        }
        return sp;
      }
    );

    const downloadPayload: CreateFHIRClientDownloadPermission = {
      isUnlimited: this.maxDocMode === 'unlimited',
      downloadLimit:
        this.maxDocMode === 'set-limit' ? this.downloadLimit : undefined,
      formatIds: this.downloadFormats
        .filter((f) => f.isEnabled)
        .map((f) => f.formatTypeId)
    };

    await Promise.all([
      this.updateFhirClient({
        id: this.client.id,
        payload: {
          isSearchActive: this.form.isSearchActive,
          isDownloadActive: this.form.isDownloadActive
        }
      }),
      this.updateClientParameters({ id: this.client.id, parameters }),
      this.updateClientDownload({
        id: this.client.id,
        payload: downloadPayload
      })
    ]);

    this.reportSaveOutcome();
  }

  private reportSaveOutcome() {
    const errors = [
      this.updateFhirClientState?.error,
      this.updateClientParametersState?.error,
      this.updateClientDownloadState?.error
    ].filter(Boolean);

    if (errors.length) {
      Toast.open({
        message: _get(
          errors[0],
          'response.data.message',
          'Failed to update API access.'
        ),
        position: 'is-top',
        type: 'is-danger'
      });
      return;
    }

    Toast.open({
      message: 'API access updated successfully.',
      position: 'is-top',
      type: 'is-success'
    });
    this.savedSnapshot = this.currentSnapshot();
  }
}
